import { Base64 } from 'js-base64';
import moment from 'moment';
import { v4 } from 'uuid';
import { Routes } from '@share/constants';
import {
  DATES_LABEL,
  SESSION_KEY_LABEL,
  ROOMS_SEARCH_LABEL,
  CHECKIN_LABEL,
  CHECKOUT_LABEL,
  CARS_DATES_LABEL,
  CARS_SESSION_KEY_LABEL,
  CONDO_IS_FLEXIBLE_LABEL,
  CONDO_DATES_LABEL,
  CONDO_DATES_FLEXIBLE_LABEL,
  CONDO_SESSION_KEY,
  CONDO_SEARCH_BOUNDS,
  CONDO_LOCATIONS_OBJECT_LABEL,
  CONDO_LOCATIONS_LABEL,
  CONDO_GUESTS_LABEL,
  QUOTE_LABEL,
} from '@share/constants';

export type SearchAgainType = 'hotel' | 'condo' | 'car' | 'activity';

const DATE_FORMAT = 'YYYY-MM-DD';
const DEFAULT_CAR_TIME = '10:30';

type Param = [string, string];

// Build the "search again" URL for an expired quote from the quote's own `link` (a deep link
// to a detail page). We keep the link's host and account path segment, swap the path to the
// engine's search route, and replace the stale date params with fresh ones:
//   - hotels / activities / cars -> tomorrow (checkout = check-in + 1 day)
//   - condos -> a Flexible + Anytime search (no explicit dates)
// The opaque priced-search session keys are dropped so the search page prices anew, and the
// `quote` marker param is removed. All other params (location, guests, rooms, filters) carry
// over so the search reopens for the same place.
export const buildSearchAgainUrl = (link: string | undefined | null, type: SearchAgainType): string => {
  if (!link) return '';

  try {
    const base = link.startsWith('http')
      ? link
      : `${window.location.origin}${link.startsWith('/') ? '' : '/'}${link}`;

    const [beforeHash] = base.split('#');
    const qIndex = beforeHash.indexOf('?');
    const pathPart = qIndex === -1 ? beforeHash : beforeHash.slice(0, qIndex);
    const queryString = qIndex === -1 ? '' : beforeHash.slice(qIndex + 1);

    const urlObj = new URL(pathPart);
    const { origin } = urlObj;
    const accountName = urlObj.pathname.split('/')[1] || '';
    const accountPath = accountName ? `/${accountName}` : '';

    // Base64 payloads are stored raw (not percent-encoded) in this app's URLs, and only the
    // first "=" splits key/value, so split manually to preserve any "=" padding in the value.
    let params: Param[] = queryString
      ? queryString
          .split('&')
          .filter(Boolean)
          .map((pair): Param => {
            const i = pair.indexOf('=');
            return i === -1 ? [pair, ''] : [pair.slice(0, i), pair.slice(i + 1)];
          })
      : [];

    const removeKeys = (keys: string[]) => {
      params = params.filter(([key]) => !keys.includes(key));
    };
    const setParam = (key: string, value: string) => {
      const idx = params.findIndex(([k]) => k === key);
      if (idx >= 0) params[idx] = [key, value];
      else params.push([key, value]);
    };
    const getParam = (key: string) => params.find(([k]) => k === key)?.[1];

    const checkIn = moment().add(1, 'day').format(DATE_FORMAT);
    const checkOut = moment().add(2, 'day').format(DATE_FORMAT);

    let route: string;

    switch (type) {
      case 'hotel':
      case 'activity': {
        route = Routes.Search;
        removeKeys([QUOTE_LABEL, SESSION_KEY_LABEL, ROOMS_SEARCH_LABEL, CHECKIN_LABEL, CHECKOUT_LABEL]);
        setParam(DATES_LABEL, Base64.encode(JSON.stringify({ startDate: checkIn, endDate: checkOut })));
        break;
      }
      case 'car': {
        route = Routes.CarsSearch;
        // Preserve the original pick-up/return times if the link carried them.
        let startTime = DEFAULT_CAR_TIME;
        let endTime = DEFAULT_CAR_TIME;
        const existing = getParam(CARS_DATES_LABEL);
        if (existing) {
          try {
            const decoded = JSON.parse(Base64.decode(existing));
            if (decoded?.startDateTime) startTime = decoded.startDateTime;
            if (decoded?.endDateTime) endTime = decoded.endDateTime;
          } catch {
            // keep defaults
          }
        }
        removeKeys([QUOTE_LABEL, CARS_SESSION_KEY_LABEL]);
        setParam(
          CARS_DATES_LABEL,
          Base64.encode(
            JSON.stringify({ startDate: checkIn, startDateTime: startTime, endDate: checkOut, endDateTime: endTime })
          )
        );
        // The cars search page only auto-runs the search when a carsSessionKey is present
        // (see CarsSearch.componentDidMount). Mirror the app's widget builder with a fresh key.
        setParam(
          CARS_SESSION_KEY_LABEL,
          Base64.encode(
            JSON.stringify({ value: v4(), isInvalid: true, expireDate: moment().format('yyyy-MM-DDTHH:mm:ss.SSSZ') })
          )
        );
        break;
      }
      case 'condo':
      default: {
        route = Routes.CondoSearch;
        // The condo quote link carries a request-shaped `condoLocationObject`: the real
        // location is nested under `.location`, alongside guest counts and check-in/out. The
        // deals search page instead expects the plain location as `condoLocationObject`, a
        // separate `condoLocationLabel` and `condoGuests`, and a Flexible + Anytime search.
        // Rebuild those and drop the request-only extras (bounds, flexible-dates, session).
        const rawLocationObject = getParam(CONDO_LOCATIONS_OBJECT_LABEL);
        let request: any = {};
        if (rawLocationObject) {
          try {
            request = JSON.parse(Base64.decode(rawLocationObject));
          } catch {
            request = {};
          }
        }
        // Fall back to the object itself if it is already a plain location (no nesting).
        const location = request?.location ?? request;
        const guests = {
          adultsCount: request?.adultsCount ?? 0,
          kidsCount: request?.childrenCount ?? 0,
          bedroomsCount: request?.bedroomsCount ?? 0,
          includeStudio: true,
          prevAdultsCount: request?.adultsCount ?? 0,
          prevKidsCount: request?.childrenCount ?? 0,
          prevBedroomsCount: request?.bedroomsCount ?? 0,
          prevIncludeStudio: true,
          error: '',
        };

        removeKeys([
          QUOTE_LABEL,
          CONDO_SESSION_KEY,
          CONDO_DATES_LABEL,
          CONDO_DATES_FLEXIBLE_LABEL,
          CONDO_SEARCH_BOUNDS,
          CONDO_LOCATIONS_OBJECT_LABEL,
          CONDO_LOCATIONS_LABEL,
          CONDO_GUESTS_LABEL,
        ]);
        setParam(CONDO_LOCATIONS_LABEL, Base64.encode(String(location?.name ?? '')));
        setParam(CONDO_LOCATIONS_OBJECT_LABEL, Base64.encode(JSON.stringify(location)));
        setParam(CONDO_DATES_LABEL, Base64.encode(JSON.stringify({})));
        setParam(CONDO_GUESTS_LABEL, Base64.encode(JSON.stringify(guests)));
        setParam(CONDO_IS_FLEXIBLE_LABEL, Base64.encode('1'));
        // The deals page only auto-runs the search when a condoSessionKey is present (see
        // CondoSearch.componentDidMount). Mirror the app's widget builder: a fresh key with
        // an already-elapsed expireDate so the page performs a new search rather than reusing.
        setParam(
          CONDO_SESSION_KEY,
          Base64.encode(
            JSON.stringify({ value: v4(), isInvalid: true, expireDate: moment().format('yyyy-MM-DDTHH:mm:ss.SSSZ') })
          )
        );
        break;
      }
    }

    const query = params.map(([k, v]) => (v === '' ? k : `${k}=${v}`)).join('&');
    return `${origin}${accountPath}${route}${query ? `?${query}` : ''}`;
  } catch (error) {
    console.error('Failed to build search-again URL', error);
    return '';
  }
};
